import React, { createContext, useContext, useEffect, useState } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { navigate } from 'gatsby';
import { auth0Config } from '../utils/auth0-config';
import { getUserMetadata } from '../utils/auth0-management';

// Create Auth0 context
export const Auth0Context = createContext();

// Auth0 provider wrapper component
export const Auth0ProviderWithHistory = ({ children }) => {
  // Check if we're in a browser environment
  const isBrowser = typeof window !== 'undefined';

  // If we're not in a browser, render children without Auth0Provider
  if (!isBrowser) {
    return children;
  }

  const onRedirectCallback = (appState) => {
    navigate(appState?.returnTo || '/');
  };

  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
        redirect_uri: auth0Config.redirectUri,
        audience: auth0Config.audience,
        scope: auth0Config.scope,
      }}
      cacheLocation={auth0Config.cacheLocation}
      onRedirectCallback={onRedirectCallback}
    >
      <Auth0ContextProvider>{children}</Auth0ContextProvider>
    </Auth0Provider>
  );
};

// Custom Auth0 context provider
export const Auth0ContextProvider = ({ children }) => {
  // Check if we're in a browser environment
  const isBrowser = typeof window !== 'undefined';

  // If we're not in a browser, render children without Auth0Context
  if (!isBrowser) {
    return children;
  }

  // Safe access to useAuth0 hook (only in browser)
  const {
    isAuthenticated,
    loginWithRedirect,
    logout,
    user,
    isLoading,
    getAccessTokenSilently,
  } = useAuth0();
  const [userMetadata, setUserMetadata] = useState(null);

  useEffect(() => {
    const fetchUserMetadata = async () => {
      if (isAuthenticated && user) {
        try {
          // Fetch user metadata from Auth0 Management API
          const metadata = await getUserMetadata(user.sub, getAccessTokenSilently);
          console.log('Fetched user metadata from Auth0:', metadata);
          setUserMetadata({
            ...user,
            ...metadata,
          });
        } catch (e) {
          console.error('Error getting user metadata:', e.message);
          // Fallback to just using the user object from Auth0
          setUserMetadata(user);
        }
      }
    };

    fetchUserMetadata();
  }, [getAccessTokenSilently, isAuthenticated, user]);

  // No localStorage fallback for favorites

  // Create a user object that matches our existing AuthContext structure
  const currentUser = isAuthenticated
    ? {
        uid: user?.sub,
        email: user?.email,
        displayName: user?.name || user?.nickname || user?.email?.split('@')[0],
        photoURL: user?.picture || `https://ui-avatars.com/api/?name=${user?.name || user?.nickname || 'User'}&background=random`,
        isPremium: userMetadata?.isPremium || false,
        alerts: userMetadata?.alerts || [],
        // Ensure favoriteLeaflets is always an array
        favoriteLeaflets: Array.isArray(userMetadata?.favoriteLeaflets) ?
          userMetadata.favoriteLeaflets :
          // Empty array if no favorites exist
          [],
      }
    : null;

  // Log the current user for debugging
  console.log('Auth0Context currentUser:', currentUser);

  // Login methods
  const loginWithEmail = async (email, password) => {
    await loginWithRedirect({
      authorizationParams: {
        login_hint: email,
      },
    });
  };

  const loginWithGoogle = async () => {
    await loginWithRedirect({
      authorizationParams: {
        connection: 'google-oauth2',
      },
    });
  };

  const loginWithFacebook = async () => {
    await loginWithRedirect({
      authorizationParams: {
        connection: 'facebook',
      },
    });
  };

  // Register method
  const registerWithEmail = async (email, password, name) => {
    await loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup',
        login_hint: email,
      },
    });
  };

  // Logout method
  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  // Check if a leaflet is a favorite
  const isFavorite = (leafletId) => {
    if (!currentUser || !Array.isArray(currentUser.favoriteLeaflets)) {
      return false;
    }
    return currentUser.favoriteLeaflets.includes(leafletId);
  };

  // Provide all authentication methods and state to the application
  const value = {
    currentUser,
    loading: isLoading,
    isPremium: currentUser?.isPremium || false,
    userAlerts: currentUser?.alerts || [],
    unreadNotifications: 0,
    loginWithEmail,
    loginWithGoogle,
    loginWithFacebook,
    registerWithEmail,
    logout: handleLogout,
    isAuthenticated,
    user,
    getAccessTokenSilently,
    isFavorite,
  };

  return <Auth0Context.Provider value={value}>{children}</Auth0Context.Provider>;
};

// Custom hook to use the Auth0 context
export const useAuth0Context = () => {
  // Check if we're in a browser environment
  const isBrowser = typeof window !== 'undefined';

  // Get the Auth0 context
  const context = useContext(Auth0Context);

  // If we're not in a browser or the context is not available, return default values
  if (!isBrowser || !context) {
    return {
      currentUser: null,
      loading: false,
      isPremium: false,
      userAlerts: [],
      unreadNotifications: 0,
      isAuthenticated: false,
      // Provide no-op functions for SSR
      loginWithEmail: () => {},
      loginWithGoogle: () => {},
      loginWithFacebook: () => {},
      registerWithEmail: () => {},
      logout: () => {},
      isFavorite: () => false,
      getAccessTokenSilently: () => Promise.resolve('')
    };
  }

  return context;
};
